import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  IconButton,
  Grid,
  useTheme,
  Chip
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import NotesIcon from '@mui/icons-material/Notes';
import dayjs from 'dayjs';
import 'dayjs/locale/ja';
import { formatPlayTime } from '../../utils/dateUtils';
import { updatePlayLogRecord, deletePlayLogRecord } from '../../services/api';
import PlaylogRecordDialog from '../dialogs/PlaylogRecordDialog';
import Tips from '../dialogs/Tips';


interface PlayLogRecord {
  date: string;
  play_time: number;
  category: string[];
  comment: string;
}

interface PlaylogCalendarProps {
  records: PlayLogRecord[];
  gameId: number;
  isOwner: boolean;
  onRecordUpdate?: (updatedRecord: PlayLogRecord) => void;
  onRecordAdd?: (newRecord: PlayLogRecord) => void;
  onRecordDelete?: (date: string) => void;
}


const PlaylogCalendar: React.FC<PlaylogCalendarProps> = ({ 
  records, 
  gameId, 
  isOwner, 
  onRecordUpdate,
  onRecordAdd,
  onRecordDelete 
}) => {
  const [currentDate, setCurrentDate] = useState(dayjs());
  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<PlayLogRecord | null>(null);
  const [isNewRecord, setIsNewRecord] = useState(false);
  
  const theme = useTheme();
  const today = dayjs();

  // 曜日のラベル
  const weekDays = ['日', '月', '火', '水', '木', '金', '土'];

  // 現在表示している月のカレンダーデータを生成
  const generateCalendarDays = () => {
    const startOfMonth = currentDate.startOf('month');
    const endOfMonth = currentDate.endOf('month');
    const startOfWeek = startOfMonth.startOf('week');
    const endOfWeek = endOfMonth.endOf('week');

    const days = [];
    let current = startOfWeek;

    while (current.isBefore(endOfWeek) || current.isSame(endOfWeek, 'day')) {
      days.push(current);
      current = current.add(1, 'day');
    }

    return days;
  };

  const calendarDays = generateCalendarDays();
  
  const handleDayClick = (date: dayjs.Dayjs) => {
    if (!isOwner || date.isAfter(today, 'day')) return;
    
    setSelectedDate(date);
    const dateString = date.format('YYYY-MM-DD');
    const record = records.find(r => r.date === dateString);
    
    if (record) {
      setEditingRecord(record);
      setIsNewRecord(false);
    } else {
      setEditingRecord({
        date: dateString,
        play_time: 0,
        category: [],
        comment: ''
      });
      setIsNewRecord(true);
    }
    
    setDialogOpen(true);
  };

  const handlePrevMonth = () => {
    setCurrentDate(prev => prev.subtract(1, 'month'));
  };

  const handleNextMonth = () => {
    setCurrentDate(prev => prev.add(1, 'month'));
  };

  const handleSave = async (recordData: PlayLogRecord) => {
    try {
      await updatePlayLogRecord(gameId, recordData);
      
      if (isNewRecord) {
        onRecordAdd?.(recordData);
      } else {
        onRecordUpdate?.(recordData);
      }
      
      setDialogOpen(false);
    } catch (error) {
      console.error('プレイログの更新に失敗しました:', error);
    }
  };

  const handleDelete = async (date: string) => {
    try {
      await deletePlayLogRecord(gameId, date);
      onRecordDelete?.(date);
      setDialogOpen(false);
    } catch (error) {
      console.error('プレイログの削除に失敗しました:', error);
    }
  };

  return (
    <>
      <Box sx={{ mb: 3 }}>

      {/* カレンダーヘッダー（月年表示＋ナビゲーション） */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between', 
        mb: 1,
        px: 1
      }}>
        <IconButton 
          onClick={handlePrevMonth}
          sx={{ 
            color: 'text.secondary',
            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.08)' }
          }}
        >
          <ChevronLeft />
        </IconButton>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Chip
            label={currentDate.format('YYYY.MM')}
            size="small"
            sx={{
              fontSize: { xs: '0.7rem', md: '0.75rem' },
              height: { xs: '22px', md: '24px' },
              backgroundColor: 'rgba(255, 255, 255, 0.02)',
              color: 'text.secondary',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              fontWeight: 500,
              '& .MuiChip-label': {
                px: 1
              }
            }}
          />
          {isOwner && (
            <Tips
              title="プレイログカレンダー"
              iconColor="rgba(255, 255, 255, 0.5)"
              content={
                <Box>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    カレンダーを使ってプレイ記録を管理できます。
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, m: 0 }}>
                    <li>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        日ごとのプレイ記録を手動で追加・編集
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        新規ログの作成時にセッションが+1されますが、作成後の編集では+1されません。
                        プレイ回数を正確に記録する場合は、プレイ開始ボタンからセッションの記録を行う必要があります。
                      </Typography>
                    </li>
                  </Box>
                </Box>
              }
            />
          )}
        </Box>
        
        <IconButton 
          onClick={handleNextMonth}
          disabled={currentDate.isSame(today, 'month')}
          sx={{ 
            color: 'text.secondary',
            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.08)' },
            '&.Mui-disabled': { opacity: 0.3 }
          }}
        >
          <ChevronRight />
        </IconButton>
      </Box>

      {/* 曜日ヘッダー */}
      <Grid container sx={{ mb: 1 }}>
        {weekDays.map((day, index) => (
          <Grid item xs key={day} sx={{ display: 'flex', justifyContent: 'center' }}>
            <Typography 
              variant="caption" 
              sx={{ 
                fontSize: '0.75rem',
                fontWeight:400,
                color: index === 0 ? '#ff6b6b' : index === 6 ? '#4dabf7' : 'text.secondary',
                py: 1
              }}
            >
              {day}
            </Typography>
          </Grid>
        ))}
      </Grid>

      {/* カレンダー本体 */}
      <Box>
        {Array.from({ length: Math.ceil(calendarDays.length / 7) }).map((_, weekIndex) => (
          <Grid container key={weekIndex} sx={{ mb: 0.25 }}>
            {calendarDays.slice(weekIndex * 7, (weekIndex + 1) * 7).map((day) => {
              const dateString = day.format('YYYY-MM-DD');
              const record = records.find(r => r.date === dateString);
              const hasRecord = !!record;
              const hasPlaytime = record && record.play_time > 0;
              const hasComment = record && record.comment.trim().length > 0;
              const isCurrentMonth = day.month() === currentDate.month();
              const isToday = day.isSame(today, 'day');
              const isWeekend = day.day() === 0 || day.day() === 6;
              const isFuture = day.isAfter(today, 'day');

              return (
                <Grid item xs key={day.format('YYYY-MM-DD')}>
                  <Box
                    onClick={() => handleDayClick(day)}
                    sx={{
                      position: 'relative',
                      height: { xs: '35px', md: '44px' },
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: isOwner && !isFuture ? 'pointer' : 'default',
                      opacity: isCurrentMonth ? 1 : 0.3,
                      backgroundColor: hasRecord 
                        ? 'rgba(217, 255, 0, 0.08)' 
                        : isToday 
                        ? 'rgba(255, 255, 255, 0.06)'
                        : 'rgba(255, 255, 255, 0.015)',
                      transition: 'all 0.2s ease',
                      '&:hover': isOwner && !isFuture ? {
                        backgroundColor: hasRecord 
                          ? 'rgba(217, 255, 0, 0.15)' 
                          : 'rgba(255, 255, 255, 0.08)',
                        transform: 'translateY(-1px)'
                      } : {},
                    }}
                  >
                    {/* 日付 */}
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontSize: { xs: '0.7rem', md: '0.8rem' },
                        lineHeight: 1,
                        fontWeight: isToday ? 700 : 500,
                        color: isFuture 
                          ? 'rgba(255, 255, 255, 0.3)'
                          : isWeekend 
                          ? (day.day() === 0 ? '#ff6b6b' : '#4dabf7')
                          : 'text.primary',
                        mb: 0.5
                      }}
                    >
                      {day.date()}
                    </Typography>

                    {/* プレイ時間表示 */}
                    {hasPlaytime && (
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          fontSize: '0.7rem',
                          lineHeight: 1,
                          fontWeight: 400,
                          color: '#d9ff00',
                          backgroundColor: 'rgba(255, 255, 255, 0.15)',
                          px: 0.4,
                          py: 0.1,
                          borderRadius: 0.5,
                          textShadow: 'none'
                        }}
                      >
                        {(record.play_time / 60).toFixed(1)}
                        <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>h</Box>
                      </Typography>
                    )}

                    {/* コメントアイコン */}
                    {hasComment && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0.5,
                          left: 0.5,
                        }}
                      >
                        <NotesIcon
                          sx={{
                            fontSize: { xs: '0.6rem', md: '0.8rem' },
                            color: 'rgba(255, 255, 255, 0.7)',
                          }}
                        />
                      </Box>
                    )}

                    {/* 今日のマーカー */}
                    {isToday && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 2,
                          right: 2,
                          width: 6,
                          height: 6,
                          backgroundColor: '#d9ff00',
                          borderRadius: '50%',
                        }}
                      />
                    )}
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        ))}
      </Box>
      
      </Box>

      {/* 編集ダイアログ */}
      <PlaylogRecordDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        record={editingRecord}
        isNewRecord={isNewRecord}
        onSave={handleSave}
        onDelete={handleDelete}
      />
    </>
  );
};

export default PlaylogCalendar;